import Phaser from "phaser";
import type { MapRuntimeDefinition } from "@ninja/game-content";
import {
  mapTransitionMarkersFor,
  type MapTransitionMarker,
} from "./mapTransitionMarkerModel.js";

const MARKER_DEPTH = 9_000;
const PASSAGE_COLOR = 0x83c98a;

export class MapTransitionMarkerRenderer {
  private readonly markers: Phaser.GameObjects.Container[] = [];
  private readonly animations: Phaser.Tweens.Tween[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    map: MapRuntimeDefinition,
  ) {
    for (const marker of mapTransitionMarkersFor(map))
      this.markers.push(this.render(marker));
  }

  destroy(): void {
    for (const animation of this.animations) animation.stop();
    this.animations.length = 0;
    for (const marker of this.markers) marker.destroy(true);
    this.markers.length = 0;
  }

  private render(marker: MapTransitionMarker): Phaser.GameObjects.Container {
    const glow = this.scene.add.ellipse(0, 4, 58, 28, PASSAGE_COLOR, 0.2);
    const ring = this.scene.add
      .ellipse(0, 4, 48, 20)
      .setStrokeStyle(2, PASSAGE_COLOR, 0.9);
    const secondRing = this.scene.add
      .ellipse(0, 4, 48, 20)
      .setStrokeStyle(2, 0xd6f2bf, 0.7);
    const direction = this.scene.add.graphics();
    direction.fillStyle(0xd6f2bf, 0.95);
    direction.fillTriangle(0, -16, -7, -7, 7, -7);
    direction.lineStyle(2, PASSAGE_COLOR, 0.95);
    direction.lineBetween(0, -5, 0, 8);
    direction.lineBetween(0, 8, -5, 3);
    direction.lineBetween(0, 8, 5, 3);

    const plate = this.scene.add
      .rectangle(0, -52, 220, 38, 0x07121c, 0.94)
      .setStrokeStyle(1, PASSAGE_COLOR, 0.9);
    const title = this.scene.add
      .text(0, -59, marker.title, {
        color: "#d6f2bf",
        fontFamily: "monospace",
        fontSize: "10px",
        fontStyle: "bold",
        align: "center",
        fixedWidth: 210,
      })
      .setOrigin(0.5);
    const detail = this.scene.add
      .text(0, -45, marker.detail, {
        color: "#f4ead6",
        fontFamily: "monospace",
        fontSize: "9px",
        align: "center",
        fixedWidth: 210,
      })
      .setOrigin(0.5);

    this.animations.push(
      this.scene.tweens.add({
        targets: ring,
        scaleX: 1.45,
        scaleY: 1.35,
        alpha: 0,
        duration: 1_400,
        repeat: -1,
      }),
      this.scene.tweens.add({
        targets: secondRing,
        scaleX: 1.45,
        scaleY: 1.35,
        alpha: 0,
        duration: 1_400,
        delay: 700,
        repeat: -1,
      }),
      this.scene.tweens.add({
        targets: glow,
        alpha: 0.38,
        duration: 850,
        yoyo: true,
        repeat: -1,
      }),
    );

    return this.scene.add
      .container(marker.x, marker.y, [
        glow,
        ring,
        secondRing,
        direction,
        plate,
        title,
        detail,
      ])
      .setDepth(MARKER_DEPTH);
  }
}
